import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, LinearProgress } from '@mui/material';
import { Order, OrderFilter, OrderStatus } from 'models/order';
import { getOrdersForDentistTemplate, getOrdersForTechnicianTemplate } from 'apollo/templates/order/query.graphql';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { orderStatusEnumToValue } from 'utils/orders';
import InfiniteScroll from 'react-infinite-scroll-component';
import StatusItem from './status-item.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
const { LAB_ID_KEY } = CONSTANTS;

type StatusColumnProps = {
    statusStep: OrderStatus;
    filters: OrderFilter;
};
const StatusColumn: FC<StatusColumnProps> = ({ statusStep, filters }: StatusColumnProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [lastOffset, setLastOffset] = useState(0);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const [orders, setOrders] = useState<Order[]>([]);

    const [
        getOrdersForDentist,
        {
            loading: ordersForDentistLoading,
            fetchMore: ordersForDentistFetchMore,
            networkStatus: ordersForDentistNetworkStatus,
        },
    ] = useLazyQuery(getOrdersForDentistTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: {
                search: filters.search,
                orderStatus: [orderStatusEnumToValue(statusStep)],
                sample: filters.sample,
                clinicId: filters.clinicId,
                stepId: filters.stepId,
                archived: filters.archived,
                production: filters.production,
                dateFilterType: filters.dateFilterType,
                dateFromTo: filters.dateFromTo,
            },
            currentLabId: Cookies.get(LAB_ID_KEY),
            limit: 6,
            offset: 0,
        },
        onCompleted: ({ ordersForDentist }) => {
            setOrders(ordersForDentist);
            setLastOffset(ordersForDentist.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [
        getOrdersForTechnician,
        {
            loading: ordersForTechnicianLoading,
            fetchMore: ordersForTechnicianFetchMore,
            networkStatus: ordersForTechnicianNetworkStatus,
        },
    ] = useLazyQuery(getOrdersForTechnicianTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: {
                search: filters.search,
                orderStatus: [orderStatusEnumToValue(statusStep)],
                sample: filters.sample,
                clinicId: filters.clinicId,
                stepId: filters.stepId,
                archived: filters.archived,
                production: filters.production,
                dateFilterType: filters.dateFilterType,
                dateFromTo: filters.dateFromTo,
            },
            currentLabId: Cookies.get(LAB_ID_KEY),
            limit: 6,
            offset: 0,
        },
        onCompleted: ({ ordersForTechnician }) => {
            setOrders(ordersForTechnician);
            setLastOffset(ordersForTechnician.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (permissions.isDentist(currentUser.type)) {
            getOrdersForDentist();
        } else {
            getOrdersForTechnician();
        }
    }, []);

    const handleFetchMore = () => {
        if (permissions.isDentist(currentUser.type)) {
            ordersForDentistFetchMore({
                variables: {
                    limit: 6,
                    offset: lastOffset,
                },
            });
        } else {
            ordersForTechnicianFetchMore({
                variables: {
                    limit: 6,
                    offset: lastOffset,
                },
            });
        }
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, orders?.length || 0);

    return (
        <Box>
            <Box
                p={2}
                sx={{
                    bgcolor: 'secondary.main',
                    color: 'secondary.contrastText',
                    borderRadius: '5px 5px 0 0',
                }}
            >
                <Typography variant="h3">{translate(statusStep)}</Typography>
            </Box>
            {(ordersForTechnicianLoading || ordersForDentistLoading) &&
            ordersForDentistNetworkStatus !== NetworkStatus.fetchMore &&
            ordersForTechnicianNetworkStatus !== NetworkStatus.fetchMore ? (
                <LinearProgress color="secondary" />
            ) : (
                <InfiniteScroll
                    style={{ padding: 0 }}
                    dataLength={orders ? orders.length : 0}
                    next={handleFetchMore}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    {orders.map((order: Order) => (
                        <StatusItem key={order._id} order={order} />
                    ))}
                </InfiniteScroll>
            )}
        </Box>
    );
};

export default StatusColumn;
